import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useTranslations } from '@/hooks/useTranslations';
import { laravelJsonFetch, LaravelJsonRequestError } from '@/lib/laravelJsonFetch';
import { cn } from '@/lib/utils';
import { AlertTriangle, CheckCircle2, FolderInput, Loader2, PlayCircle, RefreshCw, RotateCcw, Trash2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface BackfillStatus {
    mounted: boolean;
    mount_path: string;
    legacy_portal_path: string;
    file_uploaders_table_present: boolean;
    metadata_rows: number;
    has_metadata: boolean;
    local_file_count: number;
    mirror: {
        mirrored: number;
        moved: number;
        pending: number;
        failed: number;
    };
    backfill: {
        total_rows: number;
        imported_rows: number;
        unmatched: number;
        failed: number;
    };
}

interface OrphanRow {
    id: number;
    legacy_relation_id: string;
    legacy_filename: string | null;
    legacy_path: string;
    status: 'unmatched' | 'failed' | 'imported';
    notes: string | null;
    assigned_project_uuid: string | null;
    updated_at: string;
}

interface ActivityRow {
    id: number;
    level: string;
    action: string;
    message: string;
    context: Record<string, unknown> | null;
    created_at: string;
}

interface ProjectSearchRow {
    uuid: string;
    contract_number: string | null;
    contract_name: string | null;
    sharepoint_provision_status: string | null;
}

export default function LegacyBackfillCard() {
    const { t } = useTranslations();

    const [status, setStatus] = useState<BackfillStatus | null>(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [mirrorRunning, setMirrorRunning] = useState(false);
    const [backfillRunning, setBackfillRunning] = useState(false);

    const [activity, setActivity] = useState<ActivityRow[]>([]);
    const [orphans, setOrphans] = useState<OrphanRow[]>([]);
    const [orphansTotal, setOrphansTotal] = useState(0);
    const [orphanFilter, setOrphanFilter] = useState<'all' | 'unmatched' | 'failed' | 'imported'>('unmatched');
    const [orphanSearch, setOrphanSearch] = useState('');

    const [reassignTarget, setReassignTarget] = useState<OrphanRow | null>(null);
    const [reassignSearch, setReassignSearch] = useState('');
    const [reassignResults, setReassignResults] = useState<ProjectSearchRow[]>([]);
    const [reassigning, setReassigning] = useState(false);

    const pollTimerRef = useRef<number | null>(null);

    const fetchStatus = useCallback(
        async (showSpinner = false) => {
            if (showSpinner) setStatusLoading(true);
            try {
                const res = await fetch('/settings/integrations/sharepoint/legacy/status', {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = (await res.json()) as BackfillStatus;
                setStatus(data);
            } catch (err) {
                if (showSpinner) {
                    toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
                }

                console.error('LegacyBackfill: status load failed', err);
            } finally {
                if (showSpinner) setStatusLoading(false);
            }
        },
        [t],
    );

    const fetchActivity = useCallback(async () => {
        try {
            const res = await fetch('/settings/integrations/sharepoint/legacy/activity', {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            if (!res.ok) return;
            const data = (await res.json()) as { data: ActivityRow[] };
            setActivity(data.data ?? []);
        } catch {
            // silent
        }
    }, []);

    const fetchOrphans = useCallback(async () => {
        const params = new URLSearchParams();
        if (orphanFilter !== 'all') params.set('status', orphanFilter);
        if (orphanSearch.trim()) params.set('search', orphanSearch.trim());
        params.set('per_page', '25');

        try {
            const res = await fetch(`/settings/integrations/sharepoint/legacy/orphans?${params.toString()}`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            if (!res.ok) return;
            const data = (await res.json()) as { data: OrphanRow[]; total: number };
            setOrphans(data.data ?? []);
            setOrphansTotal(data.total ?? 0);
        } catch {
            // silent
        }
    }, [orphanFilter, orphanSearch]);

    useEffect(() => {
        void fetchStatus(true);
        void fetchActivity();
        void fetchOrphans();
    }, [fetchStatus, fetchActivity, fetchOrphans]);

    // Poll while a stage is running
    useEffect(() => {
        if (!mirrorRunning && !backfillRunning) {
            if (pollTimerRef.current !== null) {
                window.clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
            return;
        }
        if (pollTimerRef.current !== null) return;
        pollTimerRef.current = window.setInterval(() => {
            void fetchStatus(false);
            void fetchActivity();
            void fetchOrphans();
        }, 3000);
        return () => {
            if (pollTimerRef.current !== null) {
                window.clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [mirrorRunning, backfillRunning, fetchStatus, fetchActivity, fetchOrphans]);

    // Stop "running" indicator once mirror.pending hits zero (rough heuristic).
    useEffect(() => {
        if (!status) return;
        const idle = status.mirror.pending === 0;
        if (mirrorRunning && idle) {
            // Allow one final refresh to settle counters before stopping.
            window.setTimeout(() => setMirrorRunning(false), 1500);
        }
    }, [status, mirrorRunning]);

    const [discovering, setDiscovering] = useState(false);
    const [uploadingMetadata, setUploadingMetadata] = useState(false);
    const [cleaningUp, setCleaningUp] = useState(false);
    const metadataInputRef = useRef<HTMLInputElement>(null);

    const handleMetadataUpload = useCallback(
        async (file: File) => {
            try {
                setUploadingMetadata(true);
                const form = new FormData();
                form.append('sql_file', file);
                const res = await fetch('/settings/integrations/sharepoint/legacy/metadata', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '',
                    },
                    body: form,
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.message ?? `HTTP ${res.status}`);
                toast.success(
                    t('settings.sharepoint.legacy.toast_metadata_loaded', 'Loaded {created} new, updated {updated} rows ({errors} parse errors).')
                        .replace('{created}', String(data.mirrorCreated ?? 0))
                        .replace('{updated}', String(data.mirrorUpdated ?? 0))
                        .replace('{errors}', String(data.parseErrors ?? 0)),
                );
                void fetchStatus(false);
            } catch (err) {
                toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
            } finally {
                setUploadingMetadata(false);
            }
        },
        [t, fetchStatus],
    );

    const handleCleanup = useCallback(async () => {
        if (!window.confirm(t('settings.sharepoint.legacy.step0_cleanup_confirm', 'Delete all temporary backfill data?'))) {
            return;
        }
        try {
            setCleaningUp(true);
            const res = await laravelJsonFetch<{ mirror_rows_deleted: number; orphan_rows_deleted: number }>(
                '/settings/integrations/sharepoint/legacy/metadata',
                'DELETE',
            );
            toast.success(
                t('settings.sharepoint.legacy.toast_cleanup_done', 'Removed {mirror} mirror rows and {orphans} orphan rows.')
                    .replace('{mirror}', String(res.mirror_rows_deleted))
                    .replace('{orphans}', String(res.orphan_rows_deleted)),
            );
            void fetchStatus(false);
            void fetchOrphans();
        } catch (err) {
            toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
        } finally {
            setCleaningUp(false);
        }
    }, [t, fetchStatus, fetchOrphans]);

    const handleDiscover = useCallback(async () => {
        try {
            setDiscovering(true);
            const res = await laravelJsonFetch<{ discovered: number; updated: number; failed: number; scanned: number }>(
                '/settings/integrations/sharepoint/legacy/discover',
                'POST',
            );
            toast.success(
                t('settings.sharepoint.legacy.toast_discovered', 'Discovered {discovered} new, updated {updated}.')
                    .replace('{discovered}', String(res.discovered))
                    .replace('{updated}', String(res.updated)),
            );
            void fetchStatus(false);
        } catch (err) {
            toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
        } finally {
            setDiscovering(false);
        }
    }, [t, fetchStatus]);

    const handleStartMirror = useCallback(async () => {
        try {
            setMirrorRunning(true);
            const res = await laravelJsonFetch<{ queued: number }>('/settings/integrations/sharepoint/legacy/mirror', 'POST');
            toast.success(
                t('settings.sharepoint.legacy.toast_mirror_queued', 'Queued {count} mirror job(s).').replace('{count}', String(res.queued)),
            );
            void fetchStatus(false);
        } catch (err) {
            setMirrorRunning(false);
            const msg = err instanceof LaravelJsonRequestError ? err.message : (err as Error).message;
            if (err instanceof LaravelJsonRequestError && err.status === 409) {
                toast.error(t('settings.sharepoint.legacy.toast_mirror_not_mounted', 'Legacy storage is not mounted.'));
            } else {
                toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', msg));
            }
        }
    }, [t, fetchStatus]);

    const handleStartBackfill = useCallback(
        async (retryOnly = false) => {
            try {
                setBackfillRunning(true);
                const res = await laravelJsonFetch<{ queued: number }>('/settings/integrations/sharepoint/legacy/backfill', 'POST', {
                    retry_failed: retryOnly,
                });
                toast.success(
                    t('settings.sharepoint.legacy.toast_backfill_queued', 'Queued {count} backfill job(s).').replace('{count}', String(res.queued)),
                );
                window.setTimeout(() => setBackfillRunning(false), 30_000);
            } catch (err) {
                setBackfillRunning(false);
                const msg = err instanceof LaravelJsonRequestError ? err.message : (err as Error).message;
                if (err instanceof LaravelJsonRequestError && err.status === 409) {
                    toast.error(t('settings.sharepoint.legacy.toast_no_legacy_table', 'Legacy file_uploaders table is not imported.'));
                } else {
                    toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', msg));
                }
            }
        },
        [t],
    );

    const handleDismissOrphan = useCallback(
        async (orphan: OrphanRow) => {
            try {
                await laravelJsonFetch(`/settings/integrations/sharepoint/legacy/orphans/${orphan.id}`, 'DELETE');
                toast.success(t('settings.sharepoint.legacy.toast_dismissed', 'Orphan dismissed.'));
                void fetchOrphans();
                void fetchStatus(false);
            } catch (err) {
                toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
            }
        },
        [t, fetchOrphans, fetchStatus],
    );

    const handleRetryOrphan = useCallback(
        async (orphan: OrphanRow) => {
            try {
                await laravelJsonFetch(`/settings/integrations/sharepoint/legacy/orphans/${orphan.id}/retry`, 'POST');
                toast.success(t('settings.sharepoint.legacy.toast_retry_queued', 'Retry queued.'));
                setBackfillRunning(true);
                window.setTimeout(() => setBackfillRunning(false), 15_000);
            } catch (err) {
                toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
            }
        },
        [t],
    );

    // Project autocomplete (debounced)
    useEffect(() => {
        if (reassignTarget === null) return;
        const q = reassignSearch.trim();
        if (q === '') {
            setReassignResults([]);
            return;
        }
        const id = window.setTimeout(async () => {
            try {
                const res = await fetch(`/settings/integrations/sharepoint/legacy/projects?q=${encodeURIComponent(q)}`, {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });
                if (!res.ok) {
                    setReassignResults([]);
                    return;
                }
                const data = (await res.json()) as { data: ProjectSearchRow[] };
                setReassignResults(data.data ?? []);
            } catch {
                setReassignResults([]);
            }
        }, 250);
        return () => window.clearTimeout(id);
    }, [reassignSearch, reassignTarget]);

    const handleReassignSubmit = useCallback(
        async (projectUuid: string) => {
            if (reassignTarget === null) return;
            try {
                setReassigning(true);
                await laravelJsonFetch(`/settings/integrations/sharepoint/legacy/orphans/${reassignTarget.id}/reassign`, 'POST', {
                    project_uuid: projectUuid,
                });
                toast.success(t('settings.sharepoint.legacy.toast_reassigned', 'File moved to project.'));
                setReassignTarget(null);
                setReassignSearch('');
                setReassignResults([]);
                void fetchOrphans();
                void fetchStatus(false);
            } catch (err) {
                toast.error(t('settings.sharepoint.legacy.toast_error', 'Operation failed: {error}').replace('{error}', (err as Error).message));
            } finally {
                setReassigning(false);
            }
        },
        [reassignTarget, t, fetchOrphans, fetchStatus],
    );

    const mirrorProgress = useMemo(() => {
        if (!status) return 0;
        if (status.local_file_count === 0) return 0;
        return Math.min(100, Math.round((status.mirror.mirrored / status.local_file_count) * 100));
    }, [status]);

    const backfillProgress = useMemo(() => {
        if (!status) return 0;
        if (status.backfill.total_rows === 0) return 0;
        return Math.min(100, Math.round((status.backfill.imported_rows / status.backfill.total_rows) * 100));
    }, [status]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{t('settings.sharepoint.legacy.card_title', 'Legacy File Backfill')}</CardTitle>
                <CardDescription>
                    {t(
                        'settings.sharepoint.legacy.card_description',
                        'Import files from the previous CRM into SharePoint, attached to the right projects.',
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Counters */}
                {statusLoading ? (
                    <div className="text-muted-foreground py-4 text-center text-sm">
                        <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                    </div>
                ) : status ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <CounterTile
                            label={t('settings.sharepoint.legacy.counters.mirrored', 'Mirrored')}
                            value={status.mirror.mirrored + status.mirror.moved}
                            total={status.local_file_count}
                            progress={mirrorProgress}
                        />
                        <CounterTile
                            label={t('settings.sharepoint.legacy.counters.imported', 'Imported')}
                            value={status.backfill.imported_rows}
                            total={status.backfill.total_rows}
                            progress={backfillProgress}
                        />
                        <CounterTile
                            label={t('settings.sharepoint.legacy.counters.unmatched', 'Unmatched')}
                            value={status.backfill.unmatched}
                            tone={status.backfill.unmatched > 0 ? 'warn' : undefined}
                        />
                        <CounterTile
                            label={t('settings.sharepoint.legacy.counters.failed', 'Failed')}
                            value={status.mirror.failed + status.backfill.failed}
                            tone={status.mirror.failed + status.backfill.failed > 0 ? 'error' : undefined}
                        />
                    </div>
                ) : null}

                {/* Step 0 — Upload SQL metadata */}
                {status && (
                    <Card className="border-dashed">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold">
                                {t('settings.sharepoint.legacy.step0_title', 'Step 0 — Upload file metadata')}
                            </CardTitle>
                            <CardDescription className="text-xs">
                                {t('settings.sharepoint.legacy.step0_description', 'Drop the SQL dump containing the legacy file_uploaders table.')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {status.metadata_rows > 0 ? (
                                <p className="text-success-subtle-foreground text-xs">
                                    <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                                    {t('settings.sharepoint.legacy.step0_metadata_loaded', '{count} legacy rows linked.').replace(
                                        '{count}',
                                        String(status.metadata_rows),
                                    )}
                                </p>
                            ) : status.file_uploaders_table_present ? (
                                <p className="text-muted-foreground text-xs">
                                    <CheckCircle2 className="text-success mr-1 inline h-3.5 w-3.5" />
                                    Using existing file_uploaders table.
                                </p>
                            ) : (
                                <p className="text-muted-foreground text-xs">
                                    {t(
                                        'settings.sharepoint.legacy.step0_no_metadata',
                                        'No metadata yet. Upload your SQL or use the existing file_uploaders table if you have one.',
                                    )}
                                </p>
                            )}

                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    ref={metadataInputRef}
                                    type="file"
                                    accept=".sql,.txt"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) void handleMetadataUpload(file);
                                        e.target.value = '';
                                    }}
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={status.metadata_rows > 0 ? 'outline' : 'default'}
                                    onClick={() => metadataInputRef.current?.click()}
                                    disabled={uploadingMetadata}
                                    className="gap-2"
                                >
                                    {uploadingMetadata ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                                    {uploadingMetadata
                                        ? t('settings.sharepoint.legacy.step0_uploading', 'Parsing…')
                                        : t('settings.sharepoint.legacy.step0_action', 'Choose SQL file…')}
                                </Button>
                                {(status.metadata_rows > 0 || status.mirror.mirrored + status.mirror.moved > 0) && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => void handleCleanup()}
                                        disabled={cleaningUp}
                                        className="text-destructive hover:text-destructive gap-2"
                                    >
                                        {cleaningUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                        {t('settings.sharepoint.legacy.step0_cleanup', 'Cleanup metadata')}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 1 */}
                {status && (
                    <Card className="border-dashed">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold">
                                {t('settings.sharepoint.legacy.step1_title', 'Step 1 — Mirror legacy storage')}
                            </CardTitle>
                            <CardDescription className="text-xs">
                                {t(
                                    'settings.sharepoint.legacy.step1_description',
                                    'Uploads files from /legacy-storage to SharePoint /CRM/legacyportal/.',
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-muted-foreground">{t('settings.sharepoint.legacy.mount_path', 'Source path')}:</span>
                                <code className="bg-muted rounded px-1.5 py-0.5">{status.mount_path}</code>
                                {status.mounted ? (
                                    <Badge variant="default" className="bg-success text-success-foreground gap-1">
                                        <CheckCircle2 className="h-3 w-3" />
                                        {t('settings.sharepoint.legacy.mounted_yes', 'Mounted')} ({status.local_file_count})
                                    </Badge>
                                ) : (
                                    <Badge variant="destructive" className="gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        {t('settings.sharepoint.legacy.mounted_no', 'Not mounted')}
                                    </Badge>
                                )}
                            </div>
                            {!status.mounted && (
                                <p className="text-muted-foreground text-xs">
                                    {t(
                                        'settings.sharepoint.legacy.mount_hint',
                                        'Set LEGACY_STORAGE_HOST_PATH in .env to the host directory containing the legacy storage/app folder, then recreate the queue + web containers.',
                                    )}
                                </p>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                                <Button type="button" size="sm" onClick={() => void handleDiscover()} disabled={discovering} className="gap-2">
                                    {discovering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderInput className="h-3.5 w-3.5" />}
                                    {discovering
                                        ? t('settings.sharepoint.legacy.status_running', 'Running…')
                                        : t('settings.sharepoint.legacy.step1_discover', 'Discover from SharePoint')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void handleStartMirror()}
                                    disabled={!status.mounted || mirrorRunning}
                                    className="gap-2"
                                >
                                    {mirrorRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                                    {mirrorRunning
                                        ? t('settings.sharepoint.legacy.status_running', 'Running…')
                                        : t('settings.sharepoint.legacy.step1_action', 'Start mirror')}
                                </Button>
                            </div>
                            <p className="text-muted-foreground text-xs">
                                {t(
                                    'settings.sharepoint.legacy.step1_discover_hint',
                                    'Use this if you uploaded the legacy files into /CRM/legacyportal/ directly via SharePoint web or sync. No local mount needed.',
                                )}
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Step 2 */}
                {status && (
                    <Card className="border-dashed">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-semibold">
                                {t('settings.sharepoint.legacy.step2_title', 'Step 2 — Match & place')}
                            </CardTitle>
                            <CardDescription className="text-xs">
                                {t(
                                    'settings.sharepoint.legacy.step2_description',
                                    'Reads legacy file_uploaders rows, finds matching projects, moves files to project folders.',
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {!status.has_metadata && (
                                <p className="text-warning-subtle-foreground text-xs">
                                    {t('settings.sharepoint.legacy.status_no_table', 'Legacy SQL not imported')}
                                </p>
                            )}
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => void handleStartBackfill(false)}
                                    disabled={!status.has_metadata || backfillRunning}
                                    className="gap-2"
                                >
                                    {backfillRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                                    {backfillRunning
                                        ? t('settings.sharepoint.legacy.status_running', 'Running…')
                                        : t('settings.sharepoint.legacy.step2_action', 'Start backfill')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void handleStartBackfill(true)}
                                    disabled={!status.has_metadata || backfillRunning || status.backfill.failed === 0}
                                    className="gap-2"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    {t('settings.sharepoint.legacy.step2_retry_failed', 'Retry failed only')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Activity log */}
                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-sm font-semibold">{t('settings.sharepoint.legacy.activity_title', 'Activity (last 30)')}</h4>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                                void fetchActivity();
                                void fetchStatus(false);
                            }}
                        >
                            <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                    <Card>
                        <CardContent className="p-0">
                            {activity.length === 0 ? (
                                <p className="text-muted-foreground p-4 text-center text-xs">
                                    {t('settings.sharepoint.legacy.activity_empty', 'No activity yet.')}
                                </p>
                            ) : (
                                <ul className="max-h-60 divide-y overflow-y-auto text-xs">
                                    {activity.map((row) => (
                                        <li key={row.id} className="flex items-start gap-2 p-2">
                                            <ActivityIcon level={row.level} />
                                            <span className="text-muted-foreground font-mono whitespace-nowrap">
                                                {new Date(row.created_at).toLocaleTimeString()}
                                            </span>
                                            <span className="flex-1">{row.message}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Orphans */}
                <div>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold">
                            {t('settings.sharepoint.legacy.orphans_title', 'Orphans ({count})').replace('{count}', String(orphansTotal))}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2">
                            <Input
                                value={orphanSearch}
                                onChange={(e) => setOrphanSearch(e.target.value)}
                                placeholder={t('settings.sharepoint.legacy.orphans_search', 'Search filename, relation id, path…')}
                                className="h-8 w-56 text-xs"
                            />
                            <Select value={orphanFilter} onValueChange={(v) => setOrphanFilter(v as typeof orphanFilter)}>
                                <SelectTrigger className="h-8 w-36 text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('settings.sharepoint.legacy.orphans_filter_all', 'All statuses')}</SelectItem>
                                    <SelectItem value="unmatched">{t('settings.sharepoint.legacy.orphans_filter_unmatched', 'Unmatched')}</SelectItem>
                                    <SelectItem value="failed">{t('settings.sharepoint.legacy.orphans_filter_failed', 'Failed')}</SelectItem>
                                    <SelectItem value="imported">{t('settings.sharepoint.legacy.orphans_filter_imported', 'Imported')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => void fetchOrphans()}>
                                <RefreshCw className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            {orphans.length === 0 ? (
                                <p className="text-muted-foreground p-6 text-center text-xs">
                                    {t('settings.sharepoint.legacy.orphans_empty', 'No orphans.')}
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-xs">
                                                {t('settings.sharepoint.legacy.orphans_columns.filename', 'Filename')}
                                            </TableHead>
                                            <TableHead className="text-xs">
                                                {t('settings.sharepoint.legacy.orphans_columns.relation_id', 'Relation ID')}
                                            </TableHead>
                                            <TableHead className="text-xs">
                                                {t('settings.sharepoint.legacy.orphans_columns.status', 'Status')}
                                            </TableHead>
                                            <TableHead className="text-xs">
                                                {t('settings.sharepoint.legacy.orphans_columns.notes', 'Notes')}
                                            </TableHead>
                                            <TableHead className="text-right text-xs">
                                                {t('settings.sharepoint.legacy.orphans_columns.actions', 'Actions')}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orphans.map((orphan) => (
                                            <TableRow key={orphan.id}>
                                                <TableCell
                                                    className="max-w-[260px] truncate text-xs"
                                                    title={orphan.legacy_filename ?? orphan.legacy_path}
                                                >
                                                    {orphan.legacy_filename ?? orphan.legacy_path}
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">{orphan.legacy_relation_id}</TableCell>
                                                <TableCell className="text-xs">
                                                    <StatusBadge status={orphan.status} t={t} />
                                                </TableCell>
                                                <TableCell
                                                    className="text-muted-foreground max-w-[300px] truncate text-xs"
                                                    title={orphan.notes ?? ''}
                                                >
                                                    {orphan.notes}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        {orphan.status !== 'imported' && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 gap-1 text-xs"
                                                                onClick={() => {
                                                                    setReassignTarget(orphan);
                                                                    setReassignSearch('');
                                                                    setReassignResults([]);
                                                                }}
                                                            >
                                                                <FolderInput className="h-3 w-3" />
                                                                {t('settings.sharepoint.legacy.orphans_actions.reassign', 'Reassign…')}
                                                            </Button>
                                                        )}
                                                        {orphan.status === 'failed' && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 gap-1 text-xs"
                                                                onClick={() => void handleRetryOrphan(orphan)}
                                                            >
                                                                <RotateCcw className="h-3 w-3" />
                                                                {t('settings.sharepoint.legacy.orphans_actions.retry', 'Retry')}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive h-7 gap-1 text-xs"
                                                            onClick={() => void handleDismissOrphan(orphan)}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                            {t('settings.sharepoint.legacy.orphans_actions.dismiss', 'Dismiss')}
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </CardContent>

            {/* Reassign modal */}
            <ResponsiveModal open={reassignTarget !== null} onOpenChange={(open) => !open && setReassignTarget(null)} variant="sheet">
                <ResponsiveModalContent size="lg">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('settings.sharepoint.legacy.reassign_title', 'Reassign legacy file')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t(
                                'settings.sharepoint.legacy.reassign_description',
                                'Move "{filename}" into a different project\'s SharePoint folder.',
                            ).replace('{filename}', reassignTarget?.legacy_filename ?? reassignTarget?.legacy_path ?? '')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>
                    <ResponsiveModalBody className="space-y-3">
                        <Input
                            value={reassignSearch}
                            onChange={(e) => setReassignSearch(e.target.value)}
                            placeholder={t('settings.sharepoint.legacy.reassign_search', 'Search projects by contract number or name…')}
                        />
                        <div className="max-h-60 overflow-y-auto rounded border">
                            {reassignResults.length === 0 ? (
                                <p className="text-muted-foreground p-3 text-center text-xs">
                                    {reassignSearch.trim() === '' ? '...' : t('settings.sharepoint.legacy.reassign_no_results', 'No projects found.')}
                                </p>
                            ) : (
                                <ul className="divide-y text-sm">
                                    {reassignResults.map((p) => (
                                        <li key={p.uuid}>
                                            <button
                                                type="button"
                                                onClick={() => void handleReassignSubmit(p.uuid)}
                                                disabled={reassigning || p.sharepoint_provision_status !== 'ready'}
                                                className="hover:bg-accent flex w-full items-center justify-between rounded-md px-3 py-2 text-left disabled:opacity-50"
                                            >
                                                <span>
                                                    <span className="font-mono text-xs">{p.contract_number}</span>
                                                    {p.contract_name && <span className="text-muted-foreground ml-2 text-xs">{p.contract_name}</span>}
                                                </span>
                                                <Badge
                                                    variant={p.sharepoint_provision_status === 'ready' ? 'default' : 'secondary'}
                                                    className="text-[10px]"
                                                >
                                                    {p.sharepoint_provision_status ?? 'no-folder'}
                                                </Badge>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" size="sm" onClick={() => setReassignTarget(null)}>
                            {t('settings.sharepoint.legacy.reassign_cancel', 'Cancel')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </Card>
    );
}

function CounterTile({
    label,
    value,
    total,
    progress,
    tone,
}: {
    label: string;
    value: number;
    total?: number;
    progress?: number;
    tone?: 'warn' | 'error';
}) {
    return (
        <div className="rounded-lg border p-3">
            <div className="text-muted-foreground text-xs">{label}</div>
            <div
                className={cn(
                    'mt-1 text-2xl font-semibold',
                    tone === 'warn' && 'text-warning-subtle-foreground',
                    tone === 'error' && 'text-destructive',
                )}
            >
                {value.toLocaleString()}
                {total !== undefined && <span className="text-muted-foreground ml-1 text-sm font-normal">/ {total.toLocaleString()}</span>}
            </div>
            {progress !== undefined && (
                <div className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full">
                    <div className="bg-primary h-full transition-all" style={{ width: `${progress}%` }} />
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status, t }: { status: 'unmatched' | 'failed' | 'imported'; t: (key: string, fallback?: string) => string }) {
    if (status === 'imported') {
        return (
            <Badge variant="default" className="bg-success text-success-foreground gap-1">
                <CheckCircle2 className="h-3 w-3" />
                {t('settings.sharepoint.legacy.orphans_status.imported', 'Imported')}
            </Badge>
        );
    }
    if (status === 'failed') {
        return (
            <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" />
                {t('settings.sharepoint.legacy.orphans_status.failed', 'Failed')}
            </Badge>
        );
    }
    return (
        <Badge variant="secondary" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            {t('settings.sharepoint.legacy.orphans_status.unmatched', 'Unmatched')}
        </Badge>
    );
}

function ActivityIcon({ level }: { level: string }) {
    if (level === 'error') return <XCircle className="text-destructive h-3.5 w-3.5 shrink-0" />;
    if (level === 'warning') return <AlertTriangle className="text-warning h-3.5 w-3.5 shrink-0" />;
    return <CheckCircle2 className="text-success h-3.5 w-3.5 shrink-0" />;
}
